import { useApp } from '../state/store';
import { navigate } from '../lib/router';
import { C, R, font } from '../theme/tokens';
import { calendarItems, type CalTone, type CalView } from '../state/selectors';
import { addDays, dayLabel, monthYear, userTimeZone, weekdayShort, whenLabel, ymdIn } from '../lib/dates';
import { useNow } from '../lib/useNow';
import { Btn, Mono, Photo, Segmented, safeTop, ScreenTitle } from '../components/ui';
import { Sticker } from '../components/chrome';

const VIEWS: readonly CalView[] = ['Mine', 'My UNITs', 'Worldwide'];
const TONE: Record<CalTone, { bg: string; fg: string }> = {
  lead: { bg: C.violet, fg: C.white },
  role: { bg: C.soft, fg: C.violet },
  optional: { bg: C.divider, fg: C.grey },
  deadline: { bg: C.amberBg, fg: C.amber },
};
const PROG_TONE = {
  violet: { bg: C.violet, fg: C.white, sub: C.soft, line: 'rgba(255,255,255,.25)', stickerBg: C.ink, stickerFg: C.white },
  ink: { bg: C.ink, fg: C.white, sub: C.mutedDark, line: C.darkBorder, stickerBg: C.rim, stickerFg: C.ink },
  white: { bg: C.white, fg: C.ink, sub: C.grey, line: C.subtle, stickerBg: C.violet, stickerFg: C.white },
} as const;

export function Calendar() {
  const { state: { data, ui }, act } = useApp();
  const nowMs = useNow();
  const me = data.ambassadors[data.me];
  const tz = userTimeZone();
  const today = ymdIn(nowMs, me.timeZone);
  const { view, day } = ui.calendar;
  const items = calendarItems(data, view);
  const start = addDays(today, -3);
  const days = Array.from({ length: 35 }, (_, i) => addDays(start, i));
  const shown = items.filter((i) => (day ? i.ymd === day : i.ymd >= today));
  const groups: { ymd: string; items: typeof shown }[] = [];
  shown.forEach((i) => {
    const g = groups.find((x) => x.ymd === i.ymd);
    if (g) g.items.push(i);
    else groups.push({ ymd: i.ymd, items: [i] });
  });

  return (
    <div className="fade">
      <div style={{ padding: `${safeTop(20)} 20px 0`, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <ScreenTitle right={monthYear(day ?? today)}>Calendar</ScreenTitle>
        <Segmented label="Calendar view" options={VIEWS} value={view} onChange={(v) => act.setCalendar({ view: v })} />
      </div>

      <div className="noscroll" role="listbox" aria-label="Pick a day" style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '14px 20px 2px' }}>
        {days.map((d) => {
          const has = items.some((i) => i.ymd === d);
          const sel = day === d;
          const isToday = d === today;
          return (
            <button key={d} type="button" role="option" aria-selected={sel} aria-label={dayLabel(d)} onClick={() => act.setCalendar({ day: sel ? null : d })}
              style={{ flex: 'none', width: 48, height: 68, borderRadius: R.panel, border: `1px solid ${sel ? C.ink : isToday ? C.accentBorder : C.subtle}`, background: sel ? C.ink : isToday ? C.soft : C.white, color: sel ? C.white : isToday ? C.violet : C.ink,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, transition: 'all .2s' }}>
              <span style={{ font: font(600, 9), letterSpacing: '.1em' }}>{weekdayShort(d)}</span>
              <span style={{ font: font(700, 18, 1), letterSpacing: '-.02em' }}>{Number(d.slice(8))}</span>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: has ? (sel ? C.white : C.violet) : 'transparent' }} />
            </button>
          );
        })}
      </div>
      {day && (
        <div style={{ padding: '10px 20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ font: font(600, 13) }}>{dayLabel(day)}</span>
          <button type="button" onClick={() => act.setCalendar({ day: null })} style={{ border: 0, background: 'transparent', color: C.violet, font: font(600, 13), padding: '10px 0' }}>Show all ›</button>
        </div>
      )}

      <div className="noscroll" aria-label="Recurring programmes" style={{ display: 'flex', gap: 12, overflowX: 'auto', padding: '26px 14px 6px', scrollSnapType: 'x mandatory' }}>
        {data.programmes.map((pg, idx) => {
          const t = PROG_TONE[pg.tone];
          return (
            <section key={pg.id} style={{ position: 'relative', flex: '0 0 86%', scrollSnapAlign: 'start', background: t.bg, color: t.fg, border: pg.tone === 'white' ? `1px solid ${C.subtle}` : 0, borderRadius: R.hero, padding: 18, transform: `rotate(${idx % 2 ? 1 : -1}deg)` }}>
              <Sticker size={74} bg={t.stickerBg} fg={t.stickerFg} style={{ top: -12, right: 14, font: font(800, 9, 1.15), letterSpacing: '.08em' }}>{pg.cadence}</Sticker>
              <span style={{ font: font(700, 10), letterSpacing: '.22em', color: t.sub }}>RECURRING PROGRAMME</span>
              <h2 style={{ margin: '6px 0 12px', font: font(800, 30, 0.95), letterSpacing: '-.04em', paddingRight: 70 }}>{pg.name}</h2>
              {pg.activityIds.map((id) => {
                const a = data.activities[id];
                if (!a) return null;
                const lead = a.organiserId === data.me;
                return (
                  <button key={id} type="button" onClick={() => navigate({ name: 'activity', id })}
                    style={{ width: '100%', display: 'grid', gridTemplateColumns: '62px 1fr auto', gap: 10, alignItems: 'center', padding: '10px 0', border: 0, borderTop: `1px solid ${t.line}`, background: 'transparent', color: t.fg, textAlign: 'left', minHeight: 44 }}>
                    <Mono size={11} weight={600} color={t.fg}>{dayLabel(ymdIn(a.startsAt, a.timeZone)).slice(4).toUpperCase()}</Mono>
                    <span style={{ font: font(700, 15) }}>{a.city}</span>
                    <span style={{ font: font(700, 9), letterSpacing: '.12em', background: lead ? C.white : pg.tone === 'white' ? C.ink : C.ink, color: lead ? C.violet : C.white, padding: '5px 8px', borderRadius: R.pill }}>{lead ? 'YOU LEAD' : ['IDEA', 'PLANNING', 'SCHEDULED', 'LIVE', 'DONE'][a.stage]}</span>
                  </button>
                );
              })}
              <span style={{ display: 'block', font: font(500, 11, 1.4), color: t.sub, paddingTop: 8, borderTop: `1px solid ${t.line}` }}>{pg.description}</span>
            </section>
          );
        })}
      </div>

      <div style={{ padding: '8px 12px 0' }}>
        {groups.map((g) => (
          <div key={g.ymd} style={{ paddingTop: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ padding: '0 8px', font: font(600, 10), letterSpacing: '.22em', color: g.ymd === today ? C.violet : C.ink }}>
              {`${dayLabel(g.ymd)}${g.ymd === today ? ' · TODAY' : g.ymd === addDays(today, 1) ? ' · TOMORROW' : ''}`.toUpperCase()}
            </span>
            {g.items.map((i) => {
              const a = data.activities[i.activityId];
              const dl = i.kind === 'deadline';
              const tone = TONE[i.tag.tone];
              return (
                <button key={i.key} type="button" onClick={() => navigate({ name: 'activity', id: a.id })} className="fade"
                  style={{ display: 'grid', gridTemplateColumns: '52px 1fr auto', gap: 12, alignItems: 'center', padding: 12, borderRadius: R.pulse, border: `1px solid ${dl ? C.amberBorder : i.tag.tone === 'optional' ? C.subtle : C.accentBorder}`, background: dl ? C.amberBg : C.white, color: C.ink, textAlign: 'left' }}>
                  <span style={{ width: 52, height: 52, borderRadius: R.thumb, overflow: 'hidden', background: dl ? C.white : C.divider, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {dl ? <span style={{ font: font(800, 18), color: C.amber }}>!</span> : <Photo id={a.photo} pad={4} fallback={a.series.slice(0, 1)} sizes="52px" style={{ padding: a.photo ? undefined : 8 }} />}
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <Mono size={10} weight={600} color={dl ? C.amber : C.violet}>{dl ? 'YOUR DEADLINE' : `${a.isHunt ? 'GLOBAL HUNT · ' : ''}${a.type}`.toUpperCase()}</Mono>
                    <span style={{ font: font(700, 15, 1.2) }}>{i.title}</span>
                    <span style={{ font: font(400, 12, 1.35), color: dl ? C.amber : C.grey }}>{dl ? a.name : `${whenLabel(a, tz)} · ${a.city}`}</span>
                  </span>
                  {!dl && <span style={{ font: font(700, 9), letterSpacing: '.1em', background: tone.bg, color: tone.fg, padding: '5px 8px', borderRadius: R.pill, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.tag.label}</span>}
                </button>
              );
            })}
          </div>
        ))}
        {!groups.length && <div style={{ marginTop: 16, padding: 22, borderRadius: R.pulse, border: `1px dashed ${C.border}`, font: font(400, 14, 1.45), color: C.grey }}>Nothing in this view for that day.</div>}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 18, padding: 16, borderRadius: R.card, background: C.white, border: `1px solid ${C.subtle}` }}>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ font: font(600, 15) }}>Got an idea for this month?</span>
            <span style={{ font: font(400, 12), color: C.grey }}>Times show in venue time and your time.</span>
          </span>
          <Btn variant="ink" onClick={() => navigate({ name: 'create', sub: 'propose' })}>Propose</Btn>
        </div>
      </div>
    </div>
  );
}
